import { z } from "zod";
//#region src/dlq-reads.ts
/**
* The operator DLQ reads of site-admin-api, gate `cp:dlq:read` (`DLQ_READ_PERM`):
*
* - `GET /v1/admin/dlq?dpId=`: `DlqController.list`, the type `DlqListResponse`. The rows are
*   core-api `toCpListItem` output, merged across the placed orgs by `DlqService.list`.
* - `GET /v1/admin/dlq/census?dpId=`: `DlqController.census`, the type `DlqCensusResponse` of
*   `DlqCensusService.census`. Each row is a parsed DP census row plus `group`, `orgSlug` and
*   `orgName`.
*
* Plan `2026-10-21-operator-tui`, defaults row F1, ruling R-003. The site-admin-api declarations
* stay the producer; these strict schemas let a client refuse a body that drifted.
*/
/**
* One row of the DLQ list. `id` and `sequence` are the stream sequence as text; the next page
* starts after it. `lastAttemptAt` and `receivedAt` are the DP's timestamps, as sent.
*/
const dlqListItemSchema = z.object({
	id: z.string().min(1),
	sequence: z.string().min(1),
	subject: z.string(),
	originalSubject: z.string(),
	attemptCount: z.number().int().nonnegative(),
	lastError: z.string(),
	lastAttemptAt: z.string(),
	receivedAt: z.string()
}).strict();
/**
* `GET /v1/admin/dlq`: one page, newest first.
*
* - `nextCursor`: the cursor of the one org that answered, or `null` when none or two or more
*   answered (a merged resume would skip rows).
* - `totalCount`: the message count of the data plane's whole stream, every org, or `null` when
*   the CP could not count. It is never `0` in place of `null`, and it is not `items.length`.
*/
const dlqListResponseSchema = z.object({
	items: z.array(dlqListItemSchema),
	nextCursor: z.string().min(1).nullable(),
	totalCount: z.number().int().nonnegative().nullable()
}).strict();
/** The five census groups, in console tab order. Each group has its own cause and action. */
const DLQ_CENSUS_GROUPS = [
	"retryable",
	"unattributed",
	"org-not-placed",
	"audit-quarantine",
	"unreadable"
];
/** The largest census `limit`, and its default. */
const DLQ_CENSUS_MAX_LIMIT = 500;
/** The worker envelope of a kind `envelope` row. `dlqReason` is the `zion-dlq-reason` header, or `null`. */
const dlqCensusEnvelopeSchema = z.object({
	originalSubject: z.string(),
	orgId: z.string(),
	attempts: z.number(),
	lastError: z.string(),
	failedAt: z.string(),
	dlqReason: z.string().nullable()
}).strict();
/** The archiver advisory of a kind `audit-quarantine` row. It carries no payload. */
const dlqCensusAdvisorySchema = z.object({
	subjectOrg: z.string(),
	reason: z.string(),
	auditSubject: z.string(),
	streamSeq: z.number(),
	sha256: z.string(),
	byteLength: z.number(),
	eventId: z.string().nullable()
}).strict();
/**
* One census row.
*
* - `kind`: `envelope`, `audit-quarantine` or `unreadable`. `envelope` and `advisory` are present
*   when the DP sent them; an unreadable row has neither.
* - `group`: one of {@link DLQ_CENSUS_GROUPS}.
* - `orgSlug`: the DP org slug the row names, or `null`.
* - `orgName`: the placed tenant name, or `null` (always `null` at `limit: 1`).
*/
const dlqCensusItemSchema = z.object({
	id: z.string(),
	subject: z.string(),
	receivedAt: z.string(),
	sizeBytes: z.number(),
	kind: z.enum([
		"envelope",
		"audit-quarantine",
		"unreadable"
	]),
	envelope: dlqCensusEnvelopeSchema.optional(),
	advisory: dlqCensusAdvisorySchema.optional(),
	group: z.enum(DLQ_CENSUS_GROUPS),
	orgSlug: z.string().nullable(),
	orgName: z.string().nullable()
}).strict();
/** One group and its row count. */
const dlqCensusGroupCountSchema = z.object({
	group: z.enum(DLQ_CENSUS_GROUPS),
	count: z.number().int().nonnegative()
}).strict();
/**
* `GET /v1/admin/dlq/census`.
*
* - `streamDepth`: the whole stream count, or `null` when the DP could not describe it.
* - `scanned`: the stored messages the DP read, matching the subject filter or not.
* - `groups`: all five groups in {@link DLQ_CENSUS_GROUPS} order, a zero included. The counts are
*   the rows of each group in `items`.
* - `items`: newest first, as the DP answered.
*/
const dlqCensusResponseSchema = z.object({
	streamDepth: z.number().int().nonnegative().nullable(),
	scanned: z.number().int().nonnegative(),
	groups: z.array(dlqCensusGroupCountSchema),
	items: z.array(dlqCensusItemSchema)
}).strict().refine((body) => body.groups.map((entry) => entry.group).join(",") === DLQ_CENSUS_GROUPS.join(",") && body.groups.every((entry) => entry.count === body.items.filter((item) => item.group === entry.group).length), {
	message: "groups lists the five groups in order with the row count of each",
	path: ["groups"]
});
//#endregion
export { DLQ_CENSUS_GROUPS, DLQ_CENSUS_MAX_LIMIT, dlqCensusAdvisorySchema, dlqCensusEnvelopeSchema, dlqCensusGroupCountSchema, dlqCensusItemSchema, dlqCensusResponseSchema, dlqListItemSchema, dlqListResponseSchema };

//# sourceMappingURL=dlq-reads.js.map